export const STATUS_LABELS = {
  PLANNED: 'Planned',
  REPORT_DUE: 'Report Due',
  SUBMITTED: 'Submitted',
  SENT_BACK: 'Sent Back',
  REVIEWED: 'Reviewed',
  CANCELLED: 'Cancelled',
  RESCHEDULED: 'Rescheduled',
};

export const STATUS_COLORS = {
  PLANNED: 'bg-sky-100 text-sky-800 border-sky-300',
  REPORT_DUE: 'bg-amber-100 text-amber-800 border-amber-300',
  SUBMITTED: 'bg-violet-100 text-violet-800 border-violet-300',
  SENT_BACK: 'bg-red-100 text-red-800 border-red-300',
  REVIEWED: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  CANCELLED: 'bg-slate-100 text-slate-600 border-slate-300',
  RESCHEDULED: 'bg-slate-100 text-slate-600 border-slate-300',
};

export function statusLabel(status) {
  return STATUS_LABELS[status] || status;
}

export function statusBadgeClass(status) {
  return STATUS_COLORS[status] || 'bg-slate-100 text-slate-700 border-slate-300';
}

// Short curated list first (covers VSTL's main markets), full IANA list appended where supported.
const CURATED_TIMEZONES = [
  'Asia/Hong_Kong', 'Asia/Shanghai', 'Asia/Singapore', 'Asia/Dubai', 'Asia/Kolkata',
  'Europe/London', 'America/New_York', 'America/Los_Angeles', 'Australia/Sydney', 'UTC',
];

export function listTimezones() {
  try {
    const all = Intl.supportedValuesOf('timeZone');
    const rest = all.filter((z) => !CURATED_TIMEZONES.includes(z));
    return [...CURATED_TIMEZONES, ...rest];
  } catch {
    return CURATED_TIMEZONES;
  }
}

export function browserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function fmtDateTimeInTz(iso, tz) {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      timeZone: tz || 'UTC',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function fmtDateOnly(iso) {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(iso));
  } catch {
    return iso;
  }
}

// ISO "yyyy-MM-ddTHH:mm" (no seconds/zone), matching <input type="datetime-local">.
export function toLocalInputValue(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export const CHINA_HK_TOKENS = new Set([
  'china', 'cn', 'prc', 'peoples republic of china', 'mainland china',
  'hong kong', 'hongkong', 'hk', 'hong kong sar', 'hksar',
]);

export function isChinaHk(country) {
  if (!country) return false;
  const norm = country.trim().toLowerCase().replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, ' ').trim();
  return CHINA_HK_TOKENS.has(norm);
}

export function apiErrorText(e, fallback) {
  const d = e.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join('; ');
  return fallback;
}

// Image reduction over 2MB before upload (camera photos can be 5-15MB) — downsizes to a
// max 1920px edge and re-encodes as JPEG at declining quality until under the cap, or
// gives up after a few tries and uploads the original (server still enforces its own cap).
const MAX_UPLOAD_BYTES = 2 * 1024 * 1024;

export function compressImageIfNeeded(file) {
  if (!file.type.startsWith('image/') || file.size <= MAX_UPLOAD_BYTES) {
    return Promise.resolve(file);
  }
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, 1920 / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const tryQuality = (quality) => {
        canvas.toBlob((blob) => {
          if (!blob) { resolve(file); return; }
          if (blob.size <= MAX_UPLOAD_BYTES || quality <= 0.4) {
            resolve(new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' }));
          } else {
            tryQuality(quality - 0.15);
          }
        }, 'image/jpeg', quality);
      };
      tryQuality(0.85);
    };
    img.onerror = () => resolve(file);
    img.src = url;
  });
}
